import { AnalyticsBrowser } from '@customerio/cdp-analytics-browser';
import { TrackingUserData } from '~/lib/tracking';

// Define the Analytics interface based on what we need
interface Analytics {
  identify: (id: string, attributes: Record<string, unknown>) => void;
  track: (eventName: string, properties?: Record<string, unknown>) => void;
}

// Singleton instances and state
let customerIOInstance: Analytics | null = null;
let initPromise: Promise<Analytics | null> | null = null;

export function useCustomerIO() {
  const showTrackingLogs = process.env.NEXT_PUBLIC_SHOW_TRACKING_LOGS === 'true';

  // Initialize Customer IO with write key
  const init = async (writeKey: string) => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    // If already initialized, return the instance
    if (customerIOInstance) {
      return customerIOInstance;
    }

    // If initialization is in progress, wait for it to complete
    if (initPromise) {
      return await initPromise;
    }

    if (showTrackingLogs) console.log('Initializing Customer IO');

    // Create a promise that will resolve with the analytics instance
    initPromise = new Promise(async (resolve) => {
      try {
        // Initialize the analytics browser
        const analyticsInstance = await AnalyticsBrowser.load({
          cdnURL: 'https://cdp-eu.customer.io',
          writeKey,
        });

        // Store the first element which is the analytics API
        customerIOInstance = (analyticsInstance as [Analytics, unknown])[0];
        resolve(customerIOInstance);
      } catch (error) {
        console.error('Error initializing Customer IO:', error);
        initPromise = null;
        resolve(null);
      }
    });

    return await initPromise;
  };

  const identify = async (userId: number, userData: TrackingUserData) => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    // If initialization is in progress, wait for it to complete
    if (initPromise && !customerIOInstance) {
      try {
        await initPromise;
      } catch (error) {
        console.error('Error waiting for Customer IO initialization:', error);
        return;
      }
    }

    // If still no instance after waiting, return early
    if (!customerIOInstance) return;

    // Identify the user
    try {
      if (showTrackingLogs) console.log('Identifying in Customer IO', userId, userData);
      const userKey = userId.toString();
      customerIOInstance.identify(userKey, userData);
    } catch (error) {
      console.error('Error identifying user in Customer IO:', error);
    }
  };

  // Create a wrapper for track that handles the case where customerIO is not initialized yet
  const track = async (
    eventName: string,
    properties?: Record<string, unknown>
  ) => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    // If initialization is in progress, wait for it to complete
    if (initPromise && !customerIOInstance) {
      try {
        await initPromise;
      } catch (error) {
        console.error('Error waiting for Customer IO initialization:', error);
        return;
      }
    }

    // If still no instance after waiting, return early
    if (!customerIOInstance) return;

    // Track the event
    try {
      if (showTrackingLogs && process.env.NODE_ENV !== 'production') {
        console.log('Tracking in Customer IO', eventName, properties);
      }
      customerIOInstance.track(eventName, properties);
    } catch (error) {
      console.error('Error tracking event in Customer IO:', error);
    }
  };

  return {
    init,
    track,
    identify,
  };
}
